import { Link } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import { sitePrinciples, siteReviews } from "@/mocks/site";
import { siteStats } from "@/mocks/blacklist";

const SITE = "https://imgmmd.cn";

const LEVELS = [
  {
    level: "高危",
    color: "bg-accent-500",
    desc: "已有较多用户提交同类反馈，或平台已出现停止运营、无法提现等明显异常。",
  },
  {
    level: "中危",
    color: "bg-primary-500",
    desc: "反馈集中在退款、售后、费用等方面，问题具有重复性，但尚未达到停止运营程度。",
  },
  {
    level: "观察",
    color: "bg-secondary-500",
    desc: "近期出现少量反馈，信息尚不充分，仅作为交易前的参考提醒，持续跟进核实。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于避雷哨",
      url: `${SITE}/about`,
      inLanguage: "zh-CN",
      description:
        "避雷哨是一个面向消费者的风险信息记录平台，汇总网站黑料、避雷名单与投诉反馈，帮助用户在付款前做出更稳妥的判断。",
      mainEntity: {
        "@type": "Organization",
        name: "避雷哨",
        url: `${SITE}/`,
        description: "网站黑料风险记录库，提供避雷名单查询、防骗知识与维权指南。",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE}/about` },
      ],
    },
  ];

  return (
    <>
      <PageMeta
        title="关于我们_网站黑料风险记录库 - 避雷哨"
        description="了解避雷哨的定位、内容原则与收录标准。我们汇总用户反馈与公开信息，按风险等级整理网站黑料、避雷名单与投诉记录，仅供参考。"
        keywords="关于避雷哨,网站黑料,避雷名单,内容原则,收录标准"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100 to-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            关于避雷哨：把零散的反馈，整理成能用的参考
          </h1>
          <p className="mt-3 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            我们不是裁判，也不替任何人下结论。我们做的是一件更朴素的事——把散落在各处的用户反馈收集起来，去重、核实、按等级整理，让你在付款前多一个判断依据。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="overflow-hidden rounded-xl border border-background-200 bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20a%20documentation%20archive%20room%20with%20organized%20file%20drawers%20and%20soft%20amber%20light%2C%20muted%20warm%20charcoal%20and%20teal%20palette%2C%20minimal%20geometric%20flat%20design%2C%20soft%20gradient%20background%2C%20no%20text%2C%20no%20letters&width=1200&height=900&seq=about-archive-2026&orientation=landscape"
              alt="避雷哨风险记录整理示意"
              title="关于避雷哨 - 网站黑料风险记录库"
              className="h-64 w-full object-cover md:h-80"
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="我们在做什么"
              title="为每一次付款，多留一道判断"
              desc="很多人踩坑后才发现，其实早就有别人遇到过同样的问题，只是信息散落在各个角落。避雷哨做的事情，就是把这些碎片拼起来。"
            />
            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-3.5">
                  <dt className="font-heading text-xl font-bold text-foreground-950">{stat.value}</dt>
                  <dd className="mt-1 font-label text-xs text-foreground-500">{stat.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="内容原则"
            title="我们自己给自己立的规矩"
            desc="信息类平台最怕的就是失真。以下四条，是我们在整理每一条记录时都会遵守的底线。"
          />
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {sitePrinciples.map((item) => (
              <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="收录标准"
          title="风险等级是怎么划分的"
          desc="等级不是判决，而是对反馈数量与异常程度的初步归类。查看记录时，请结合等级一起理解。"
        />
        <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
          {LEVELS.map((item) => (
            <div key={item.level} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className={`h-1.5 w-full ${item.color}`} />
              <div className="p-6">
                <span className={`inline-flex rounded-full px-3 py-1 font-label text-xs font-medium text-background-50 ${item.color}`}>
                  {item.level}
                </span>
                <p className="mt-4 font-body text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 rounded-lg border border-background-200 bg-background-100 px-5 py-4 font-label text-xs leading-relaxed text-foreground-500">
          说明：风险等级会随反馈变化而调整。被收录不代表已经认定违规，也不代表平台当前一定存在问题；未被收录同样不代表没有风险。请始终以实际交易体验与官方渠道信息为准。
        </p>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <SectionHeading eyebrow="用户口碑" title="他们是这样用避雷哨的" align="center" />
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {siteReviews.map((review) => (
              <figure key={review.name} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                <i className="ri-double-quotes-l text-2xl text-primary-300" />
                <blockquote className="mt-3 flex-1 font-body text-sm leading-relaxed text-foreground-700">
                  {review.content}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3.5">
                  <p className="font-label text-sm font-medium text-foreground-900">{review.name}</p>
                  <p className="mt-0.5 font-label text-xs text-foreground-500">
                    {review.role} · {review.date}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-error-warning-line text-primary-600" />
              免责声明
            </h2>
            <div className="mt-4 space-y-3 font-body text-sm leading-relaxed text-foreground-600">
              <p>
                本站内容由用户提交与公开信息整理而成，仅作为消费决策的参考，不构成对任何个人或企业的事实认定、法律评价或投资建议。
              </p>
              <p>
                平台经营状况可能随时变化，本站不对信息的完整性、及时性作出保证。任何基于本站内容作出的决策，其后果由使用者自行承担。
              </p>
              <p>
                如你认为某条记录存在错误或侵犯了你的合法权益，可通过联系页面提交更正申请与证明材料，我们会在核实后及时更新或下架。
              </p>
            </div>
          </div>
          <div className="flex flex-col justify-center rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">有线索，或者需要更正？</h2>
            <p className="mt-3 font-body text-sm leading-relaxed text-foreground-700">
              提交后我们会在 24 小时内完成初步核实。你的隐私信息仅用于联系核实，不会公开展示。
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-add-circle-line text-base" />
                提交线索或申请更正
              </Link>
              <Link
                to="/blacklist"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-5 py-3 font-label text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
              >
                <i className="ri-list-check-2 text-base" />
                浏览避雷名单
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}